const format = require('date-fns/format');
const markdownItAST = require('markdown-it-ast');

const fm = require('./front-matter');
const EventEmitter = require('events');
const markdownItFactory = require('./markdown-it-factory');

class MarkdownContent extends EventEmitter {
  constructor(parsed, md, title, filepath, autolink, baseLevel, removeHeader = false, stackSecionLabel = []) {
    super();

    this.parsed = parsed;
    this.md = md;
    this.title = title;
    this.filepath = filepath;
    this.autolink = autolink;
    this.baseLevel = baseLevel;
    this.removeHeader = removeHeader;
    this.stackSecionLabel = stackSecionLabel;
  }

  extractSections() {
    const result = {};
    let currentSectionLevel = this.baseLevel;
    let currentSectionLabel = '_';
    let currentSectionParses = [];
    const sectionLabelStack = [];

    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'section_open') {
        currentSectionLevel++;
        if (currentSectionLevel - this.baseLevel === 2 && currentSectionParses.length > 0) {
          result._ = new MarkdownContent(
            currentSectionParses,
            this.md,
            this.title,
            this.filepath,
            this.autolink,
            currentSectionLevel
          ).toString();
          currentSectionParses = [];
        }

        const attrs = new Map(this.parsed[i].attrs);
        currentSectionLabel = attrs.get('data-section-id');
        sectionLabelStack.push(currentSectionLabel);
      }

      if (this.parsed[i].type === 'section_close') {
        currentSectionLevel--;
        currentSectionLabel = sectionLabelStack.pop();

        if (currentSectionLevel - this.baseLevel === 1) {
          result[currentSectionLabel] = new MarkdownContent(
            currentSectionParses,
            this.md,
            this.title,
            this.filepath,
            this.autolink,
            currentSectionLevel,
            true,
            sectionLabelStack.concat(currentSectionLabel).slice(1)
          );
          currentSectionParses = [];
        }
      }

      if (this.parsed[i].tag !== 'section' || currentSectionLevel - this.baseLevel > 1) {
        currentSectionParses.push(this.parsed[i]);
      }
    }

    // Process parse results of section-less contents
    if (currentSectionParses.length > 0 && !result._) {
      result._ = new MarkdownContent(
        currentSectionParses,
        this.md,
        this.title,
        this.filepath,
        this.autolink,
        currentSectionLevel
      ).toString();
    }

    Object.assign(this, result);

    return result;
  }

  toString() {
    const parsed = this.removeHeader ? this.parsed.slice(1, this.parsed.length) : this.parsed;
    const content = this.md.dump(parsed, this.stackSecionLabel);
    this.emit('render', { content: this, dumped: content });

    return this.md.renderer.render(content, this.md.options);
  }
}

class MarkdownParser extends EventEmitter {
  constructor(
    game,
    input,
    filepath,
    autolink,
    isApplySectionId = true,
    baseLevel = 0,
    withTokenMarker = false,
    withSupportWebP = false,
    imageSizes = {},
    tooltips = {}
  ) {
    super();

    const { attributes, body } = fm(input);

    if (attributes.updated_at && /^\s*\d\d\d\d-\d\d-\d\d \d\d:\d\d\s*$/.test(attributes.updated_at)) {
      attributes.updated_at = format(new Date(attributes.updated_at), 'YYYY-MM-DD HH:mm');
    } else {
      attributes.updated_at = undefined;
    }

    this.meta = attributes;
    this.game = game;

    this.isApplySectionId = isApplySectionId;
    this.baseLevel = baseLevel;
    if (autolink) {
      this.autolink = autolink;
      this.autolink.game = game;
    }

    this.filepath = filepath;
    this.title = null;
    this.toc = null;

    this.md = markdownItFactory(withTokenMarker, withSupportWebP, imageSizes, tooltips);
    this.parsed = this.md.load(body, game, this.autolink, this.filepath);
  }

  get helperScripts() {
    return this.md.helperScripts;
  }

  setTitle(title) {
    this.title = title;
  }

  getTitle() {
    if (this.title) {
      return this.title;
    }

    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'section_open') {
        const attrs = new Map(this.parsed[i].attrs);
        if (attrs.get('data-level') !== '1') {
          continue;
        }

        this.title = attrs.get('data-section-id');

        return this.title;
      }
    }
  }

  getTOC() {
    if (this.toc) {
      return this.toc;
    }

    let isTOCOpen = false;

    const tocTokens = [];

    for (let i = 0; i < this.parsed.length; i++) {
      if (isTOCOpen && this.parsed[i].type === 'section_close') {
        break;
      }

      if (!isTOCOpen && this.parsed[i].type === 'section_open') {
        const attrs = new Map(this.parsed[i].attrs);
        if (attrs.get('data-section-id') !== '目次') {
          continue;
        }

        isTOCOpen = true;
        continue;
      }

      if (isTOCOpen) {
        tocTokens.push(this.parsed[i]);
      }
    }

    const ast = markdownItAST.makeAST(tocTokens);
    this.toc = this.parseTOC(ast);
    return this.toc;
  }

  parseTOC(ast) {
    function parseListItem(token) {
      const result = {
        link: '',
        text: '',
        textIncludingImg: [],
      };

      const paragraph = token.children.find((c) => c.nodeType === 'paragraph');
      if (!paragraph) {
        return result;
      }

      const inline = paragraph.children.find((c) => c.type === 'inline');
      if (!inline) {
        return result;
      }
      const textData = inline.children;
      const existImg = textData.some((c) => c.tag === 'img');
      if (existImg) {
        textData.forEach((c) => {
          const type = c.type;
          if (type === 'text') {
            const obj = { text: c.content };
            result.textIncludingImg.push(obj);
            return;
          }

          if (type === 'link_open') {
            const attrs = new Map(c.attrs);
            result.link = attrs.get('href');
            return;
          }

          if (type === 'image') {
            const obj = {};
            obj.img = {};
            obj.img.src = c.attrs[0][1];
            obj.img.alt = c.attrs[1][1];
            result.textIncludingImg.push(obj);
            return;
          }
        });
      } else {
        textData.forEach((c) => {
          const type = c.type;
          if (type === 'link_open') {
            const attrs = new Map(c.attrs);
            result.link = attrs.get('href');
            return;
          }

          if (type === 'text') {
            result.text = c.content;
            return;
          }
        });
      }

      return result;
    }

    const items = [];

    let currentItem = {
      link: '',
      text: '',
      textIncludingImg: [],
      children: [],
    };

    ast.forEach((node) => {
      if (node.nodeType === 'bullet_list') {
        items.push(...this.parseTOC(node.children));
      }

      if (node.nodeType === 'list_item') {
        currentItem = {
          link: '',
          text: '',
          children: [],
        };

        const { link, text, textIncludingImg } = parseListItem(node);
        currentItem.link = link;
        currentItem.text = text;
        currentItem.textIncludingImg = textIncludingImg;

        if (node.children.length > 0) {
          currentItem.children = this.parseTOC(node.children);
        }

        items.push(currentItem);

        return;
      }
    });

    return items;
  }

  getFirstImageUrl() {
    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'inline' && this.parsed[i].children) {
        for (let j = 0; j < this.parsed[i].children.length; j++) {
          const target = this.parsed[i].children[j];
          if (target.type === 'image') {
            const attrs = new Map(target.attrs);
            return attrs.get('src');
          }
        }
      }
    }
    return '';
  }

  getImageUrls() {
    const urls = [];
    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'inline' && this.parsed[i].children) {
        for (let j = 0; j < this.parsed[i].children.length; j++) {
          const target = this.parsed[i].children[j];
          if (target.type === 'image') {
            const attrs = new Map(target.attrs);
            urls.push(attrs.get('src'));
          }
        }
      }
    }
    return urls;
  }

  getArticleBody() {
    let body = '';
    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'inline' && this.parsed[i].children) {
        for (let j = 0; j < this.parsed[i].children.length; j++) {
          const target = this.parsed[i].children[j];
          if (target.type === 'text') {
            body += target.content;
          }
        }
      }
    }
    return body;
  }

  getLeadText() {
    let sectionLength = 0;
    let insideTable = false;
    let lead = '';

    for (let i = 0; i < this.parsed.length; i++) {
      if (this.parsed[i].type === 'section_open') {
        sectionLength++;
      }
      if (sectionLength > 1) {
        return lead;
      }
      if (this.parsed[i].type === 'table_open') {
        insideTable = true;
      }
      if (this.parsed[i].type === 'table_close') {
        insideTable = false;
      }
      if (this.parsed[i].type === 'inline' && this.parsed[i].children && insideTable === false) {
        for (let j = 0; j < this.parsed[i].children.length; j++) {
          const target = this.parsed[i].children[j];
          if (target.type === 'text') {
            lead += target.content;
          }
        }
      }
    }
    return '';
  }

  getFirstParagraphText() {
    for (let i = 0; i < this.parsed.length; i++) {
      const j = i + 1;
      if (this.parsed[i].tag === 'p' && this.parsed[j].type === 'inline') {
        return this.parsed[j].content;
      }
    }
    return '';
  }

  setFilepath(filepath) {
    this.filepath = filepath;
  }

  filterFirstLineH1Tag() {
    const firstLineIndex = 0;
    const dataLevelIndex = 1;
    const dataLevelNumberIndex = 1;
    const expectedDataLevelNumber = '1';
    if (!this.parsed[firstLineIndex]) {
      return;
    }
    if (!this.parsed[firstLineIndex].attrs) {
      return;
    }
    if (!this.parsed[firstLineIndex].attrs[dataLevelIndex]) {
      return;
    }
    if (!this.parsed[firstLineIndex].attrs[dataLevelIndex][dataLevelNumberIndex]) {
      return;
    }
    if (this.parsed[firstLineIndex].attrs[dataLevelIndex][dataLevelNumberIndex] !== expectedDataLevelNumber) {
      return;
    }

    this.parsed.shift();
  }

  renderEntireSections(removeFirstLine = true) {
    if (removeFirstLine) {
      return this.md.renderEntireSections(
        this.parsed.slice(1, this.parsed.length),
        this.game,
        this.getTitle(),
        this.filepath,
        this.autolink,
        this.baseLevel
      );
    }

    this.filterFirstLineH1Tag();

    return this.md.renderEntireSections(
      this.parsed,
      this.game,
      this.getTitle(),
      this.filepath,
      this.autolink,
      this.baseLevel
    );
  }

  renderBySections(baseLevel = 0) {
    const content = new MarkdownContent(
      this.parsed,
      this.md,
      this.getTitle(),
      this.filepath,
      this.autolink,
      baseLevel || this.baseLevel
    );

    return content.extractSections();
  }
}

module.exports = MarkdownParser;
